import React from 'react';
import { Activity, Clock, Navigation } from 'lucide-react';

const ActivityDetails = ({ details }) => {
  if (!details) return null;

  const { activity, zones } = details;

  const formatTime = (secs) => {
    if (!secs) return '00:00';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 
      ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      : `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Render Zones (Z1-Z5)
  const renderZones = () => {
    if (!zones || zones.length === 0) return null;

    // Strava usually returns 5 zones for heartrate
    // distribution_buckets: { max, min, time }
    const totalTime = zones.reduce((acc, z) => acc + (z.time || 0), 0);
    if (totalTime === 0) return <p className="text-muted text-xs">No hay datos de tiempo en zonas.</p>;

    const zoneLabels = ['Z1 Recuperación', 'Z2 Resistencia', 'Z3 Ritmo/Tempo', 'Z4 Umbral', 'Z5 Anaeróbico'];
    const zoneColors = ['#d1d5db', '#fca5a5', '#f87171', '#ef4444', '#b91c1c']; // Gray to Dark Red

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
          <Activity size={14} /> Tiempo en Zonas (FC)
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {zones.map((z, idx) => {
            const timeStr = formatTime(z.time);
            const pct = ((z.time / totalTime) * 100).toFixed(1);
            const label = zoneLabels[idx] || `Z${idx + 1}`;
            const color = zoneColors[idx] || '#ef4444';
            
            return (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '100px 70px 60px 1fr', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span style={{ color: 'var(--text-muted)' }}>{z.min || 0} - {z.max || '+'}</span>
                <span style={{ fontWeight: 600 }}>{timeStr}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontWeight: 700, width: '35px', textAlign: 'right' }}>{pct}%</span>
                  <div style={{ flex: 1, height: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Splits/Laps
  const renderSplits = () => {
    // splits_metric for running, or laps for cycling
    const laps = activity.splits_metric || activity.laps || [];
    if (!laps || laps.length === 0) return null;

    const isCycling = (activity.sport_type || activity.type || '').toLowerCase().match(/ride|bike|ciclismo/);

    return (
      <div>
        <h4 style={{ fontSize: '0.9rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
          <Navigation size={15} style={{ color: 'var(--strava-orange)' }} /> Parciales {isCycling ? '(Vueltas)' : '(por km)'}
        </h4>
        <div style={{ position: 'relative', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}>
            <table style={{ width: '100%', minWidth: '320px', fontSize: '0.8rem', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}>
                  <th style={{ padding: '0.6rem 0.4rem', fontWeight: 600 }}>KM / Lap</th>
                  <th style={{ padding: '0.6rem 0.4rem', fontWeight: 600 }}>{isCycling ? 'Velocidad' : 'Ritmo'}</th>
                  <th style={{ padding: '0.6rem 0.4rem', fontWeight: 600 }}>Desnivel</th>
                  <th style={{ padding: '0.6rem 0.4rem', fontWeight: 600 }}>FC Media</th>
                </tr>
              </thead>
              <tbody>
                {laps.map((lap, idx) => {
                  const distStr = (lap.distance / 1000).toFixed(2);
                  const elevStr = Math.round(lap.elevation_difference || lap.total_elevation_gain || 0) + ' m';
                  const hrStr = lap.average_heartrate ? Math.round(lap.average_heartrate) + ' ppm' : '--';
                  
                  let speedPaceStr = '--';
                  if (lap.average_speed > 0) {
                    if (isCycling) {
                      speedPaceStr = (lap.average_speed * 3.6).toFixed(1) + ' km/h';
                    } else {
                      const paceSecs = 1000 / lap.average_speed;
                      const mins = Math.floor(paceSecs / 60);
                      const secs = Math.floor(paceSecs % 60);
                      speedPaceStr = `${mins}:${secs.toString().padStart(2, '0')} /km`;
                    }
                  }

                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '0.6rem 0.4rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                        {lap.split || idx + 1} <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', fontWeight: 400 }}>({distStr}km)</span>
                      </td>
                      <td style={{ padding: '0.6rem 0.4rem', fontWeight: 700, color: 'var(--text-primary)' }}>{speedPaceStr}</td>
                      <td style={{ padding: '0.6rem 0.4rem', color: (lap.elevation_difference || 0) > 0 ? 'var(--strava-orange)' : 'rgba(255,255,255,0.7)' }}>{elevStr}</td>
                      <td style={{ padding: '0.6rem 0.4rem', color: 'rgba(255,255,255,0.85)' }}>{hrStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const zonesNode = renderZones();
  const splitsNode = renderSplits();

  if (!zonesNode && !splitsNode) {
    return (
      <div style={{ marginTop: '0.75rem', padding: '1.25rem', textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px dashed var(--glass-border)', borderRadius: '12px' }}>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
          No hay datos detallados de parciales o zonas cardíacas para esta actividad de Strava.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(255,255,255,0.015)',
      border: '1px solid var(--glass-border)',
      borderRadius: '12px',
      padding: '1.25rem',
      marginTop: '0.75rem',
      animation: 'fadeInTab 0.3s ease-out'
    }}>
      {zonesNode}
      {splitsNode}
    </div>
  );
};

export default ActivityDetails;
